/* The compact scheduling cell in the bindings table: two labelled lines, Exec and Trigger.
 *
 * **One column carrying two facts, because the question a reader scans this table for is one
 * question.** "Does this binding run without me" has two possible yeses — a clock and a harvest —
 * and a reader comparing twelve bindings needs both answers on one line each, not two columns of
 * mostly "Not configured". The full cards are on the rule's own page; this is the overview.
 *
 * `Exec` and `Trigger` are labelled rather than inferred from order, because a row where only one
 * is configured would otherwise leave a reader guessing which of the two the value belongs to.
 *
 * **This cell fetches nothing, and that is the whole of a production incident.** It used to call
 * `useExecutionSchedule` and `useExecutionTrigger` itself — which reads well, and means a table of
 * N rows opens 2N concurrent requests, each holding a database session for its whole life. Eight
 * bindings exhausted the API's connection pool and every read past it raised after the checkout
 * timeout. The screen now asks once for the page and hands each cell its answer, so the request
 * count is a constant rather than a function of the customer's estate.
 */

import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import type { SchedulingSummary } from '@/shared/api/generated/rules';

export interface SchedulingCellProps {
  /** This binding's answer, or `undefined` when the page's read has not landed.
   *
   * Deliberately not optional-with-a-default: the caller must decide what it is passing, because
   * "the request failed" and "this binding has nothing scheduled" render differently and only the
   * caller knows which it holds. The batch route answers with an entry per id it was given —
   * including `configured: false` — so a gap here can only ever be a failed or in-flight read.
   */
  readonly scheduling: SchedulingSummary | undefined;
}

export function SchedulingCell({ scheduling }: SchedulingCellProps): ReactElement {
  const { t } = useTranslation();
  const schedule = scheduling?.schedule;
  const trigger = scheduling?.trigger;

  /* **A read that has not landed says so, and does not say "Not configured".** The two are
     different claims — one is about this binding, the other about the request — and rendering an
     in-flight or refused read as an absence is the conflation §9 records shipping on three
     screens. A 403 lands here too, which is right: a reader without the grant is told the answer
     is unavailable rather than that nothing is scheduled. */
  const execLabel = schedule
    ? schedule.configured
      ? (schedule.describes ?? t('scheduling.cell.configured'))
      : t('scheduling.cell.none')
    : t('scheduling.cell.unknown');
  const trigLabel = trigger
    ? trigger.configured
      ? (trigger.describes ?? t('scheduling.cell.configured'))
      : t('scheduling.cell.none')
    : t('scheduling.cell.unknown');

  /* Green only when it is configured **and on**. A stored-but-disabled schedule is muted with
     every other absence, because from this column's point of view — does this binding run without
     me — a disabled schedule and no schedule have the same answer. The card on the rule's page is
     where the difference between a decision and an absence is stated. */
  const execOn = schedule !== undefined && schedule.configured && schedule.enabled;
  const trigOn = trigger !== undefined && trigger.configured && trigger.enabled;

  return (
    <div className="lq-sched__cell">
      <div className="lq-sched__cellrow">
        <span className="lq-sched__cellkey">{t('scheduling.cell.exec')}</span>
        <span className={execOn ? 'lq-sched__cellon' : 'lq-sched__celloff'}>
          {execLabel}
        </span>
      </div>
      <div className="lq-sched__cellrow">
        <span className="lq-sched__cellkey">{t('scheduling.cell.trigger')}</span>
        <span className={trigOn ? 'lq-sched__cellon' : 'lq-sched__celloff'}>
          {trigLabel}
        </span>
      </div>
    </div>
  );
}
